import { InfoDialogComponent, InfoDialogData } from '@/components/info-dialog/info-dialog.component';
import { ReferralAddDialogComponent } from '@/components/referral-add-dialog/referral-add-dialog.component';
import { ReferralHelper } from '@/helpers/referral.helper';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReferralService, ReferralSummary } from '@eemo/api-client';
import { BehaviorSubject, Observable } from 'rxjs';

/** id of the "Mon abonnement" card of the profile, target of the "Abonnement annuel requis" dialog */
export const PROFILE_SUBSCRIPTION_ANCHOR = 'my-subscription';

/**
 * Shares the referral summary of the current agent between the "Parrainage" card of the profile and the referral
 * dialogs, so that an action made in a dialog (new referee, payment request) updates every view without reloading.
 */
@Injectable({
  providedIn: 'root',
})
export class ReferralStateService {
  private referralService = inject(ReferralService);
  private dialog = inject(MatDialog);

  private summarySubject = new BehaviorSubject<ReferralSummary>(null);
  summary$: Observable<ReferralSummary> = this.summarySubject.asObservable();

  get summary(): ReferralSummary {
    return this.summarySubject.value;
  }

  refresh(): void {
    this.referralService.getCurrentUserReferralSummary().subscribe((summary) => this.summarySubject.next(summary));
  }

  /** true when the agent can't declare a new referral because the pending referral limit is reached */
  static isLimitReached(summary: ReferralSummary): boolean {
    return !!summary && summary.pendingReferralCount >= summary.maxPendingReferrals;
  }

  /** "+" button: the add form for an eligible agent, otherwise the "Abonnement annuel requis" dialog (RG-3) */
  addReferral(): void {
    if (!this.summary?.eligible) {
      this.showSubscriptionRequired();
    } else if (!ReferralStateService.isLimitReached(this.summary)) {
      this.dialog.open(ReferralAddDialogComponent, { width: '30rem', maxWidth: '95vw' });
    }
  }

  /** "Abonnement annuel requis" dialog, which leads to the "Mon abonnement" card */
  showSubscriptionRequired(): void {
    const data: InfoDialogData = {
      title: ReferralHelper.SUBSCRIPTION_REQUIRED_TITLE,
      content: ReferralHelper.SUBSCRIPTION_REQUIRED_CONTENT,
      okButtonText: 'Voir mon abonnement',
    };
    this.dialog
      .open(InfoDialogComponent, { data })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.dialog.closeAll();
          document.getElementById(PROFILE_SUBSCRIPTION_ANCHOR)?.scrollIntoView({ behavior: 'smooth' });
        }
      });
  }
}
